import React from "react";
import { branch } from "baobab-react/higher-order";
import PropTypes from "baobab-react/prop-types";

import env from "../constants/env";

import { Checkbox, Button } from "react-bootstrap";

import { uploadAiFile } from "../actions/portalActions";
import { assistantsFilesCreate, assistantsFilesDelete, filesDelete, getChatCompletions, getChatCompletionsLambda, getContentStreamed } from "../ai/aiAssistant";
import { copyToClipboard, createMarkupForLabel } from "../utils/utils";
import {
	buildDraftAssistantPrompt,
	buildScriptAssistantPrompt,
	DRAFT_ASSISTANT_MODE_FIELD_TEXT,
	DRAFT_RESPONSE_FORMAT_CANDIDATES,
	DRAFT_RESPONSE_FORMAT_MARKDOWN_BULLETS,
	DEFAULT_ASSISTANT_CHAT_MODEL,
	extractCodeFromAssistantResponse,
	SCRIPT_ASSISTANT_MODE_FUNCTIONS,
	SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER
} from "../ai/assistantPromptBuilder";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as smd from "../../node_modules/streaming-markdown/smd.js";

import state from "../state/state.js";
import { getOrgId } from "../actions/orgsActions.js";

let parser;
const AUTHORING_CONTEXT_INDEX_URL = "/assistant-context/assistant-context.index.json";
const AUTHORING_CONTEXT_SUMMARY_URL = "/assistant-context/assistant-context.summary.md";
const MAX_AUTHORING_CONTEXT_CHARS = 24000;
const MAX_AUTHORING_MATCHED_PROPERTIES = 28;
const MAX_AUTHORING_METHODS = 70;
const MAX_SCRIPT_CONTEXT_CHARS = 7000;
const MAX_SCRIPT_FUNCTIONS_CHARS = 2200;
const MAX_SCRIPT_VALUE_TRIGGER_CHARS = 420;
const MAX_SCRIPT_VALUE_TRIGGER_ENTRIES = 24;
const STREAM_SETTLE_DELAY_MS = 1200;
const MAX_WAIT_FOR_RESPONSE_MS = 45000;
const MAX_RESPONSE_SNIPPET_PREVIEW_CHARS = 1400;

class AiAssistant extends React.PureComponent {
	constructor(props, context) {
		super(props, context);

		this.state = {
			inputAiMessage: "",
			threadId: "",
			selectedActivityOrFiles: "org",
			authoringContextStatus: "idle",
			authoringContextError: "",
			isWaitingForResponse: false,
			hasReceivedResponseChunk: false,
			scriptContext: null,
			draftContext: null,
			lastResponseMode: "",
			responseViewTab: "response",
			modePanelCollapsed: false
		};

		this.authoringContextIndex = null;
		this.authoringContextSummary = "";
		this.authoringContextLoadPromise = null;
		this.responseSettleTimeoutId = null;
		this.responseMaxWaitTimeoutId = null;
		this.lastHandledLaunchRequestId = "";
		this.lastFocusedExternalEditableElement = null;
		this.preferFocusedInputForDraftApply = false;

		this.handleDocumentFocusIn = this.handleDocumentFocusIn.bind(this);
	}

	getCurrentActivityId() {
		return this.props.activityId || state.get(["tree", "root", "id"]) || "";
	}

	isWritableExternalInputElement(target) {
		if (typeof document === "undefined") {
			return false;
		}
		if (!target || !document.body || !document.body.contains(target)) {
			return false;
		}
		if (this.assistantRootEl && this.assistantRootEl.contains(target)) {
			return false;
		}

		const tag = target.tagName ? target.tagName.toUpperCase() : "";
		if (tag !== "INPUT" && tag !== "TEXTAREA") {
			return false;
		}
		if (target.readOnly || target.disabled) {
			return false;
		}

		if (tag === "INPUT") {
			const inputType = (target.type || "").toLowerCase();
			const allowedTypes = ["", "text", "search", "url", "tel", "email", "password", "number"];
			if (allowedTypes.indexOf(inputType) === -1) {
				return false;
			}
		}

		return true;
	}

	getExternalContentEditableElement(target) {
		if (typeof document === "undefined") {
			return null;
		}
		if (!target || !document.body || !document.body.contains(target)) {
			return null;
		}

		let current = target;
		while (current && current !== document.body) {
			if (this.assistantRootEl && this.assistantRootEl.contains(current)) {
				return null;
			}
			if (current.isContentEditable) {
				return current;
			}
			current = current.parentElement;
		}

		return null;
	}

	getFocusedExternalEditableSource() {
		const inputTarget = this.getWritableFocusedInputTarget();
		if (inputTarget) {
			return {
				type: "input",
				element: inputTarget,
				value: typeof inputTarget.value === "string" ? inputTarget.value : ""
			};
		}

		if (typeof document === "undefined") {
			return null;
		}

		const candidates = [
			this.lastFocusedExternalEditableElement,
			document.activeElement
		];

		for (const candidate of candidates) {
			const editableTarget = this.getExternalContentEditableElement(candidate);
			if (!editableTarget || (this.assistantRootEl && this.assistantRootEl.contains(editableTarget))) {
				continue;
			}
			const value = typeof editableTarget.innerText === "string"
				? editableTarget.innerText
				: (typeof editableTarget.textContent === "string" ? editableTarget.textContent : "");
			return {
				type: "contentEditable",
				element: editableTarget,
				value: value || ""
			};
		}

		return null;
	}

	handleDocumentFocusIn(event) {
		const target = event && event.target ? event.target : null;
		if (!target) {
			return;
		}

		if (this.assistantRootEl && this.assistantRootEl.contains(target)) {
			return;
		}

		const isEditableInput = this.isWritableExternalInputElement(target);
		const contentEditableTarget = this.getExternalContentEditableElement(target);
		const isContentEditable = !!contentEditableTarget;
		if (!isEditableInput && !isContentEditable) {
			return;
		}

		const nextFocusedElement = isEditableInput ? target : contentEditableTarget;
		if (this.lastFocusedExternalEditableElement === nextFocusedElement) {
			return;
		}
		this.lastFocusedExternalEditableElement = nextFocusedElement;
	}

	isActivityModeEnabled() {
		if (typeof this.props.allowActivityMode === "boolean") {
			return this.props.allowActivityMode;
		}
		if (typeof window === "undefined" || !window.location) {
			return false;
		}
		return window.location.pathname.indexOf("/editor/") > -1;
	}

	isFilesModeEnabled() {
		if (typeof this.props.allowFilesMode === "boolean") {
			return this.props.allowFilesMode;
		}
		return this.isActivityModeEnabled();
	}

	isAuthoringModeEnabled() {
		if (typeof this.props.allowAuthoringMode === "boolean") {
			return this.props.allowAuthoringMode;
		}
		return this.isActivityModeEnabled();
	}

	isScriptFunctionsModeEnabled() {
		return this.isActivityModeEnabled() && this.getCurrentActivityId() !== "";
	}

	isScriptValueTriggerModeEnabled() {
		if (!this.isActivityModeEnabled() || this.getCurrentActivityId() === "") {
			return false;
		}
		if (this.state.scriptContext &&
			this.state.scriptContext.mode === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER &&
			Array.isArray(this.state.scriptContext.targetPath)) {
			return true;
		}
		const selectedNodePath = state.get(["selectedNodePath"]);
		if (!Array.isArray(selectedNodePath) || selectedNodePath.length === 0) {
			return false;
		}
		const selectedNode = state.get(selectedNodePath);
		return !!(selectedNode && selectedNode.entity && selectedNode.entity.type === "item");
	}

	isFieldDraftModeEnabled() {
		return this.isAuthoringModeEnabled();
	}

	getAvailableModes() {
		const modes = [];
		if (this.isAuthoringModeEnabled()) {
			modes.push("authoring");
		}
		if (this.isFieldDraftModeEnabled()) {
			modes.push(DRAFT_ASSISTANT_MODE_FIELD_TEXT);
		}
		if (this.isScriptFunctionsModeEnabled()) {
			modes.push(SCRIPT_ASSISTANT_MODE_FUNCTIONS);
		}
		if (this.isScriptValueTriggerModeEnabled()) {
			modes.push(SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER);
		}
		if (this.isActivityModeEnabled() && this.getCurrentActivityId() !== "") {
			modes.push("activity");
		}
		modes.push("org");
		if (this.isFilesModeEnabled() && this.props.aiAssistantId && this.props.aiAssistantId !== "") {
			modes.push("files");
		}
		return modes;
	}

	ensureSelectedMode() {
		const availableModes = this.getAvailableModes();
		if (!availableModes.includes(this.state.selectedActivityOrFiles)) {
			this.setState({
				selectedActivityOrFiles: availableModes[0] || "org"
			});
			return false;
		}
		return true;
	}

	isScriptMode(mode) {
		return mode === SCRIPT_ASSISTANT_MODE_FUNCTIONS || mode === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER;
	}

	isDraftMode(mode) {
		return mode === DRAFT_ASSISTANT_MODE_FIELD_TEXT;
	}

	getSelectedNodeEntity() {
		const selectedNodePath = state.get(["selectedNodePath"]);
		if (!Array.isArray(selectedNodePath) || selectedNodePath.length === 0) {
			return { nodePath: [], entity: null };
		}
		const selectedNode = state.get(selectedNodePath);
		return {
			nodePath: selectedNodePath,
			entity: selectedNode && selectedNode.entity ? selectedNode.entity : null
		};
	}

	getDefaultScriptContext(mode) {
		if (mode === SCRIPT_ASSISTANT_MODE_FUNCTIONS) {
			return {
				mode,
				targetPath: ["tree", "root", "entity", "functions"],
				targetLabel: "Activity Functions",
				targetReadOnly: false,
				featureName: "Functions",
				featureDescription: "Activity-level reusable script functions.",
				additionalContextLines: [
					"This is the activity-level Functions script.",
					"Use AmbiFi runtime API through af.* methods."
				]
			};
		}

		if (mode === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER) {
			const selected = this.getSelectedNodeEntity();
			const selectedEntity = selected.entity || {};
			if (!selected.entity || selected.entity.type !== "item") {
				return null;
			}
			const itemId = selectedEntity.id || selectedEntity.guid || "";
			return {
				mode,
				targetPath: [...selected.nodePath, "entity", "valueTrigger"],
				targetLabel: itemId ? `Value Trigger for ${itemId}` : "Selected Item Value Trigger",
				targetReadOnly: false,
				featureName: "Value Trigger",
				featureDescription: "Item-level script triggered as value changes.",
				additionalContextLines: [
					`Current item type: ${selectedEntity.type || "(none)"}`,
					`Current item label 1: ${selectedEntity.label || "(none)"}`,
					`Current item label 2: ${selectedEntity.label2 || "(none)"}`,
					"Avoid recursive write loops when using af.setValue in Value Trigger."
				]
			};
		}

		return null;
	}

	getScriptContextForMode(mode) {
		const stateContext = this.state.scriptContext;
		const requestedContext = stateContext && stateContext.mode === mode ? stateContext : null;
		let baseContext = requestedContext;
		if (!baseContext || !Array.isArray(baseContext.targetPath) || baseContext.targetPath.length === 0) {
			baseContext = this.getDefaultScriptContext(mode);
		}

		if (!baseContext || !Array.isArray(baseContext.targetPath) || baseContext.targetPath.length === 0) {
			return null;
		}

		const currentScriptRaw = state.get(baseContext.targetPath);
		const currentScript = typeof currentScriptRaw === "string" ? currentScriptRaw : "";

		return {
			...baseContext,
			currentScript
		};
	}

	getActivityScriptingInventory() {
		const rootEntity = state.get(["tree", "root", "entity"]);
		if (!rootEntity || typeof rootEntity !== "object") {
			return {
				activityName: "",
				functionsScript: "",
				counts: { lists: 0, sections: 0, items: 0 },
				valueTriggers: []
			};
		}

		const counts = {
			lists: 0,
			sections: 0,
			items: 0
		};
		const valueTriggers = [];
		const functionsScript = typeof rootEntity.functions === "string" ? rootEntity.functions : "";
		const activityName = typeof rootEntity.name === "string"
			? rootEntity.name
			: (typeof rootEntity.title === "string" ? rootEntity.title : "");

		const visitEntity = (entity, context = {}) => {
			if (!entity || typeof entity !== "object") {
				return;
			}

			const entityType = typeof entity.type === "string" ? entity.type : "";
			const isItem = entityType === "item" || entityType.indexOf("item") === 0;
			let nextContext = context;

			if (entityType === "list") {
				counts.lists += 1;
				nextContext = {
					...context,
					listName: typeof entity.name === "string" ? entity.name : (typeof entity.title === "string" ? entity.title : ""),
					listId: entity.id || entity.guid || ""
				};
			} else if (entityType === "section") {
				counts.sections += 1;
				nextContext = {
					...context,
					sectionName: typeof entity.name === "string" ? entity.name : (typeof entity.title === "string" ? entity.title : ""),
					sectionId: entity.id || entity.guid || ""
				};
			} else if (isItem) {
				counts.items += 1;
				const rawValueTrigger = typeof entity.valueTrigger === "string" ? entity.valueTrigger : "";
				const valueTrigger = rawValueTrigger.trim();
				if (valueTrigger !== "") {
					valueTriggers.push({
						itemId: entity.id || entity.guid || "",
						itemType: entityType,
						label1: typeof entity.label === "string" ? entity.label : (typeof entity.label1 === "string" ? entity.label1 : ""),
						label2: typeof entity.label2 === "string" ? entity.label2 : "",
						listName: nextContext.listName || "",
						sectionName: nextContext.sectionName || "",
						script: rawValueTrigger
					});
				}
			}

			if (Array.isArray(entity.children) && entity.children.length > 0) {
				entity.children.forEach((child) => visitEntity(child, nextContext));
			}
		};

		visitEntity(rootEntity, {});

		return {
			activityName,
			functionsScript,
			counts,
			valueTriggers
		};
	}

	extractScriptTargetItemId(scriptContext = null) {
		if (!scriptContext || scriptContext.mode !== SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER) {
			return "";
		}
		const label = typeof scriptContext.targetLabel === "string" ? scriptContext.targetLabel : "";
		const match = label.match(/Value Trigger for (.+)$/);
		return match && match[1] ? match[1].trim() : "";
	}

	truncateScriptContextText(text = "", maxChars = 400) {
		if (!text || typeof text !== "string") {
			return "";
		}
		if (text.length <= maxChars) {
			return text;
		}
		const headChars = Math.max(0, Math.floor((maxChars - 42) / 2));
		const tailChars = Math.max(0, maxChars - headChars - 42);
		return `${text.slice(0, headChars)}\n/* ... truncated ... */\n${text.slice(text.length - tailChars)}`;
	}

	getPromptTokenSet(message = "") {
		const tokens = (message || "").toLowerCase().match(/[a-z0-9_/-]{2,}/g) || [];
		return new Set(tokens);
	}

	buildActivityScriptingPromptContext(scriptContext = null, userMessage = "") {
		const inventory = this.getActivityScriptingInventory();
		const tokenSet = this.getPromptTokenSet(userMessage);
		const functionsScript = inventory.functionsScript || "";
		const valueTriggersRaw = Array.isArray(inventory.valueTriggers) ? inventory.valueTriggers : [];
		const activeItemId = this.extractScriptTargetItemId(scriptContext);
		const currentScript = scriptContext && typeof scriptContext.currentScript === "string" ? scriptContext.currentScript.trim() : "";
		const functionsScriptTrimmed = functionsScript.trim();

		let currentScriptMatchedAsValueTrigger = false;
		const valueTriggers = valueTriggersRaw.filter((entry) => {
			if (!entry || typeof entry.script !== "string") {
				return false;
			}

			const entryScriptTrimmed = entry.script.trim();
			const isActiveItem = activeItemId !== "" && entry.itemId === activeItemId;
			if (isActiveItem) {
				return false;
			}

			if (!currentScriptMatchedAsValueTrigger &&
				scriptContext &&
				scriptContext.mode === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER &&
				currentScript !== "" &&
				entryScriptTrimmed === currentScript) {
				currentScriptMatchedAsValueTrigger = true;
				return false;
			}

			return true;
		});

		const scoreEntry = (entry) => {
			let score = 0;
			const itemId = (entry.itemId || "").toLowerCase();
			const label1 = (entry.label1 || "").toLowerCase();
			const label2 = (entry.label2 || "").toLowerCase();
			const listName = (entry.listName || "").toLowerCase();
			const sectionName = (entry.sectionName || "").toLowerCase();
			const script = (entry.script || "").toLowerCase();

			tokenSet.forEach((token) => {
				if (token.length < 3) {
					return;
				}
				if (itemId.indexOf(token) > -1) {
					score += 8;
				}
				if (label1.indexOf(token) > -1 || label2.indexOf(token) > -1) {
					score += 6;
				}
				if (listName.indexOf(token) > -1 || sectionName.indexOf(token) > -1) {
					score += 3;
				}
				if (script.indexOf(token) > -1) {
					score += 1;
				}
			});

			return score;
		};

		const rankedValueTriggers = valueTriggers
			.map((entry) => ({
				...entry,
				score: scoreEntry(entry)
			}))
			.sort((a, b) => {
				if (b.score !== a.score) {
					return b.score - a.score;
				}
				return (a.itemId || "").localeCompare(b.itemId || "");
			});

		const sectionParts = [];
		let totalChars = 0;
		const appendSection = (text) => {
			if (!text || typeof text !== "string") {
				return false;
			}
			const trimmed = text.trim();
			if (!trimmed) {
				return false;
			}

			const separator = sectionParts.length > 0 ? 2 : 0;
			if (totalChars + separator + trimmed.length <= MAX_SCRIPT_CONTEXT_CHARS) {
				sectionParts.push(trimmed);
				totalChars += separator + trimmed.length;
				return true;
			}

			const remaining = MAX_SCRIPT_CONTEXT_CHARS - totalChars - separator;
			if (remaining <= 96) {
				return false;
			}

			const clipped = `${trimmed.slice(0, remaining - 24)}\n...[context truncated]`;
			sectionParts.push(clipped);
			totalChars = MAX_SCRIPT_CONTEXT_CHARS;
			return false;
		};

		appendSection([
			"Activity scripting inventory:",
			`- Activity: ${inventory.activityName || "(unnamed activity)"}`,
			`- Lists: ${inventory.counts.lists}, Sections: ${inventory.counts.sections}, Items: ${inventory.counts.items}`,
			`- Functions script present: ${functionsScriptTrimmed ? "yes" : "no"}`,
			`- Non-empty Value Triggers: ${valueTriggersRaw.length}`
		].join("\n"));

		const shouldIncludeFunctionsScript = !!functionsScriptTrimmed &&
			!(scriptContext && scriptContext.mode === SCRIPT_ASSISTANT_MODE_FUNCTIONS && currentScript === functionsScriptTrimmed);
		if (shouldIncludeFunctionsScript) {
			appendSection([
				"Activity Functions script context:",
				this.truncateScriptContextText(functionsScript, MAX_SCRIPT_FUNCTIONS_CHARS)
			].join("\n"));
		}

		let includedValueTriggers = 0;
		let omittedValueTriggers = 0;
		for (let i = 0; i < rankedValueTriggers.length; i++) {
			if (includedValueTriggers >= MAX_SCRIPT_VALUE_TRIGGER_ENTRIES) {
				omittedValueTriggers += (rankedValueTriggers.length - i);
				break;
			}

			const entry = rankedValueTriggers[i];
			const meta = [
				`itemId=${entry.itemId || "(none)"}`,
				`type=${entry.itemType || "(item)"}`,
				entry.label1 ? `label1=${entry.label1}` : "",
				entry.label2 ? `label2=${entry.label2}` : "",
				entry.listName ? `list=${entry.listName}` : "",
				entry.sectionName ? `section=${entry.sectionName}` : ""
			].filter(Boolean).join(" | ");

			const didAppend = appendSection([
				`Value Trigger context (${includedValueTriggers + 1}): ${meta}`,
				this.truncateScriptContextText(entry.script, MAX_SCRIPT_VALUE_TRIGGER_CHARS)
			].join("\n"));

			if (!didAppend) {
				omittedValueTriggers += (rankedValueTriggers.length - i);
				break;
			}
			includedValueTriggers += 1;
		}

		if (omittedValueTriggers > 0) {
			appendSection(`Additional value trigger scripts omitted due to prompt limits: ${omittedValueTriggers}.`);
		}

		const contextSummaryLines = [
			`Activity scripting context attached: functions=${functionsScriptTrimmed ? "yes" : "no"}, valueTriggers=${valueTriggersRaw.length}.`,
			`Included value trigger script contexts in prompt: ${includedValueTriggers}${omittedValueTriggers > 0 ? ` (${omittedValueTriggers} omitted for size)` : ""}.`
		];

		return {
			activityScriptingContext: sectionParts.join("\n\n"),
			contextSummaryLines
		};
	}

	getDefaultDraftContext(mode) {
		if (mode !== DRAFT_ASSISTANT_MODE_FIELD_TEXT) {
			return null;
		}

		return {
			mode,
			targetPath: null,
			targetLabel: "",
			targetFieldType: "text_plain",
			responseFormat: DRAFT_RESPONSE_FORMAT_CANDIDATES,
			targetReadOnly: false,
			featureName: "Authoring Drafts",
			featureDescription: "Draft and rewrite authoring content for selected properties.",
			additionalContextLines: []
		};
	}

	getDraftContextForMode(mode) {
		const stateContext = this.state.draftContext;
		const requestedContext = stateContext && stateContext.mode === mode ? stateContext : null;
		const baseContextRaw = requestedContext || this.getDefaultDraftContext(mode);
		const baseContext = baseContextRaw ? { ...baseContextRaw } : null;
		if (!baseContext) {
			return null;
		}

		const hasExplicitRequestedTargetPath = !!(
			requestedContext &&
			Array.isArray(requestedContext.targetPath) &&
			requestedContext.targetPath.length > 0
		);
		const focusedTarget = this.props.aiAssistantFocusedTarget || state.get(["appState", "aiAssistantFocusedTarget"]);
		if (!hasExplicitRequestedTargetPath &&
			focusedTarget &&
			Array.isArray(focusedTarget.targetPath) &&
			focusedTarget.targetPath.length > 0) {
			const sameTargetPath = Array.isArray(baseContext.targetPath) &&
				baseContext.targetPath.length === focusedTarget.targetPath.length &&
				baseContext.targetPath.every((segment, idx) => segment === focusedTarget.targetPath[idx]);
			baseContext.targetPath = focusedTarget.targetPath;
			baseContext.targetLabel = focusedTarget.targetLabel || baseContext.targetLabel || "";
			baseContext.targetFieldType = focusedTarget.targetFieldType || baseContext.targetFieldType || "text_plain";
			baseContext.targetReadOnly = focusedTarget.targetReadOnly === true;
			baseContext.featureName = focusedTarget.targetLabel || baseContext.featureName || "";
			if (!sameTargetPath) {
				baseContext.additionalContextLines = [];
			}
		}

		let currentValue = "";
		if (Array.isArray(baseContext.targetPath) && baseContext.targetPath.length > 0) {
			const currentValueRaw = state.get(baseContext.targetPath);
			currentValue = typeof currentValueRaw === "string" ? currentValueRaw : "";
		}

		return {
			...baseContext,
			currentValue
		};
	}

	resolveDraftResponseFormat(question = "", draftContext = null) {
		const questionLower = (question || "").toLowerCase();
		const asksForBullets =
			/\bbullets?\b/.test(questionLower) ||
			/\bbullet[-\s]?list\b/.test(questionLower) ||
			/\bmarkdown\s+bullets?\b/.test(questionLower);

		if (asksForBullets) {
			return DRAFT_RESPONSE_FORMAT_MARKDOWN_BULLETS;
		}

		const requestedFormat = draftContext && typeof draftContext.responseFormat === "string"
			? draftContext.responseFormat
			: "";
		if (requestedFormat && requestedFormat !== DRAFT_RESPONSE_FORMAT_MARKDOWN_BULLETS) {
			return requestedFormat;
		}

		return DRAFT_RESPONSE_FORMAT_CANDIDATES;
	}

	handleLaunchRequest(request = null) {
		if (!request || !request.requestId || request.requestId === this.lastHandledLaunchRequestId) {
			return;
		}
		this.lastHandledLaunchRequestId = request.requestId;

		const requestedMode = request.mode;
		const nextState = {};
		if (typeof request.prefillQuestion === "string") {
			nextState.inputAiMessage = request.prefillQuestion;
		}

		if (this.isScriptMode(requestedMode)) {
			nextState.selectedActivityOrFiles = requestedMode;
			nextState.scriptContext = {
				mode: requestedMode,
				targetPath: Array.isArray(request.targetPath) ? request.targetPath : null,
				targetLabel: request.targetLabel || "",
				targetReadOnly: request.targetReadOnly === true,
				featureName: request.featureName || "",
				featureDescription: request.featureDescription || "",
				additionalContextLines: Array.isArray(request.additionalContextLines) ? request.additionalContextLines : []
			};
			this.setState(nextState, () => {
				this.ensureAuthoringContextLoaded();
				if (request.autoRun === true && (this.state.inputAiMessage || "").trim() !== "") {
					this.runScriptModeRequest(requestedMode);
				}
			});
		} else if (this.isDraftMode(requestedMode)) {
			this.preferFocusedInputForDraftApply = false;
			nextState.selectedActivityOrFiles = requestedMode;
			nextState.draftContext = {
				mode: requestedMode,
				targetPath: Array.isArray(request.targetPath) ? request.targetPath : null,
				targetLabel: request.targetLabel || "",
				targetFieldType: request.targetFieldType || "text_plain",
				responseFormat: request.responseFormat || DRAFT_RESPONSE_FORMAT_CANDIDATES,
				targetReadOnly: request.targetReadOnly === true,
				featureName: request.featureName || "",
				featureDescription: request.featureDescription || "",
				additionalContextLines: Array.isArray(request.additionalContextLines) ? request.additionalContextLines : []
			};
			this.setState(nextState, () => {
				this.ensureAuthoringContextLoaded();
				if (request.autoRun === true && (this.state.inputAiMessage || "").trim() !== "") {
					this.runDraftModeRequest(requestedMode);
				}
			});
		} else if (requestedMode === "authoring" || requestedMode === "activity" || requestedMode === "org" || requestedMode === "files") {
			nextState.selectedActivityOrFiles = requestedMode;
			this.setState(nextState, () => {
				if (requestedMode === "authoring") {
					this.ensureAuthoringContextLoaded();
				}
			});
		}

		this.context.tree.set(["appState", "aiAssistantLaunchRequest"], null);
	}

	async runDraftModeRequest(mode) {
		const availableModes = this.getAvailableModes();
		if (!availableModes.includes(mode)) {
			this.appendAssistantErrorMessage("Selected draft mode is not available on this screen.");
			return;
		}

		const draftContext = this.getDraftContextForMode(mode);
		if (!draftContext) {
			this.appendAssistantErrorMessage("Draft context is not available.");
			return;
		}

		const userMessage = (this.state.inputAiMessage || "").trim();
		if (!userMessage) {
			return;
		}

		const liveFocusedInput = this.getWritableFocusedInputTarget();
		const hasLiveFocusedInput = !!liveFocusedInput;
		const liveFocusedInputValue = hasLiveFocusedInput && typeof liveFocusedInput.value === "string"
			? liveFocusedInput.value
			: "";
		const focusedTargetState = this.props.aiAssistantFocusedTarget || state.get(["appState", "aiAssistantFocusedTarget"]);
		const hasFocusedTargetPath = !!(
			focusedTargetState &&
			Array.isArray(focusedTargetState.targetPath) &&
			focusedTargetState.targetPath.length > 0
		);
		const hasExplicitDraftTargetPath = Array.isArray(draftContext.targetPath) && draftContext.targetPath.length > 0;
		const shouldUseLiveFocusedInput = hasLiveFocusedInput && !hasExplicitDraftTargetPath;
		const effectiveCurrentValue = shouldUseLiveFocusedInput ? liveFocusedInputValue : (draftContext.currentValue || "");
		const effectiveTargetLabel = shouldUseLiveFocusedInput ? "" : (draftContext.targetLabel || "");
		const effectiveFeatureName = shouldUseLiveFocusedInput ? "" : (draftContext.featureName || "");
		const effectiveAdditionalContextLines = (shouldUseLiveFocusedInput && !hasFocusedTargetPath)
			? []
			: (Array.isArray(draftContext.additionalContextLines) ? draftContext.additionalContextLines : []);

		const resolvedResponseFormat = this.resolveDraftResponseFormat(userMessage, draftContext);

		await this.ensureAuthoringContextLoaded();
		const prompt = buildDraftAssistantPrompt({
			question: userMessage,
			currentValue: effectiveCurrentValue,
			targetLabel: effectiveTargetLabel,
			targetFieldType: draftContext.targetFieldType || "text_plain",
			responseFormat: resolvedResponseFormat,
			featureName: effectiveFeatureName,
			featureDescription: draftContext.featureDescription || "",
			contextIndex: this.authoringContextIndex,
			contextSummary: this.authoringContextSummary,
			additionalContextLines: effectiveAdditionalContextLines
		});

		this.appendConversationHeader(userMessage, mode);
		this.startWaitingForResponse();

		try {
			const chatCompletionsTemplate = {
				messages: [
					{
						role: "system",
						content: "You are a helpful assistant."
					},
					{
						role: "user",
						content: prompt
					}
				],
				model: DEFAULT_ASSISTANT_CHAT_MODEL
			};

			const draftResponse = await getChatCompletionsLambda(JSON.stringify(chatCompletionsTemplate));
			const responseText = draftResponse && typeof draftResponse === "string"
				? draftResponse
				: "I was unable to generate a response.";

			let newData = this.context.tree.get(["appState", "aiAssistantResponse"]);
			newData = newData + responseText;
			this.context.tree.set(["appState", "aiAssistantResponse"], newData);
			this.context.tree.set(["appState", "aiAssistantResponseLast"], responseText);
		} catch (_err) {
			this.appendAssistantErrorMessage("Failed to process drafting response.");
		} finally {
			this.stopWaitingForResponse();
		}
	}

	async runScriptModeRequest(mode) {
		const availableModes = this.getAvailableModes();
		if (!availableModes.includes(mode)) {
			this.appendAssistantErrorMessage("Selected script mode is not available on this screen.");
			return;
		}

		const scriptContext = this.getScriptContextForMode(mode);
		if (!scriptContext || !Array.isArray(scriptContext.targetPath)) {
			this.appendAssistantErrorMessage("No valid script target was found for this mode.");
			return;
		}

		const userMessage = (this.state.inputAiMessage || "").trim();
		if (!userMessage) {
			return;
		}

		await this.ensureAuthoringContextLoaded();
		const activityScriptContext = this.buildActivityScriptingPromptContext(scriptContext, userMessage);
		const baseAdditionalContextLines = Array.isArray(scriptContext.additionalContextLines)
			? scriptContext.additionalContextLines
			: [];
		const combinedAdditionalContextLines = [
			...baseAdditionalContextLines,
			...(Array.isArray(activityScriptContext.contextSummaryLines) ? activityScriptContext.contextSummaryLines : [])
		];
		const prompt = buildScriptAssistantPrompt({
			mode,
			question: userMessage,
			currentScript: scriptContext.currentScript || "",
			featureName: scriptContext.featureName || "",
			featureDescription: scriptContext.featureDescription || "",
			contextIndex: this.authoringContextIndex,
			contextSummary: this.authoringContextSummary,
			additionalContextLines: combinedAdditionalContextLines,
			activityScriptingContext: activityScriptContext.activityScriptingContext || ""
		});

		this.appendConversationHeader(userMessage, mode);
		this.startWaitingForResponse();

		try {
			const chatCompletionsTemplate = {
				messages: [
					{
						role: "system",
						content: "You are a helpful assistant."
					},
					{
						role: "user",
						content: prompt
					}
				],
				model: DEFAULT_ASSISTANT_CHAT_MODEL
			};

			const scriptResponse = await getChatCompletionsLambda(JSON.stringify(chatCompletionsTemplate));
			const responseText = scriptResponse && typeof scriptResponse === "string"
				? scriptResponse
				: "I was unable to generate a response.";

			let newData = this.context.tree.get(["appState", "aiAssistantResponse"]);
			newData = newData + responseText;
			this.context.tree.set(["appState", "aiAssistantResponse"], newData);
			this.context.tree.set(["appState", "aiAssistantResponseLast"], responseText);
		} catch (_err) {
			this.appendAssistantErrorMessage("Failed to process script generation response.");
		} finally {
			this.stopWaitingForResponse();
		}
	}

	insertScriptFromAssistant({ replace = false } = {}) {
		const mode = this.state.selectedActivityOrFiles;
		if (!this.isScriptMode(mode)) {
			return;
		}

		const scriptContext = this.getScriptContextForMode(mode);
		if (!scriptContext || !Array.isArray(scriptContext.targetPath)) {
			this.appendAssistantErrorMessage("No valid script target is selected.");
			return;
		}
		if (scriptContext.targetReadOnly) {
			this.appendAssistantErrorMessage("This target is read-only and cannot be updated.");
			return;
		}

		const aiResponse = this.props.aiAssistantResponseLast || "";
		const extractedCode = extractCodeFromAssistantResponse(aiResponse);
		if (!extractedCode) {
			this.appendAssistantErrorMessage("No JavaScript code block was found in the latest assistant response. Ask for script generation to enable Insert/Replace.");
			return;
		}

		const currentValueRaw = state.get(scriptContext.targetPath);
		const currentValue = typeof currentValueRaw === "string" ? currentValueRaw : "";
		const nextCode = `${extractedCode.trim()}\n`;
		const nextValue = replace
			? nextCode
			: `${currentValue}${currentValue && !currentValue.endsWith("\n") ? "\n" : ""}${nextCode}`;

		this.context.tree.set(scriptContext.targetPath, nextValue);
		this.bumpEditorRefreshCounter();
	}

	bumpEditorRefreshCounter() {
		const currentRefresh = state.get(["appState", "forceEditorRefresh"]);
		const nextRefresh = typeof currentRefresh === "number" ? currentRefresh + 1 : 1;
		state.set(["appState", "forceEditorRefresh"], nextRefresh);
	}

	resolveDraftApplyTargetContext() {
		const mode = this.state.selectedActivityOrFiles;
		const draftContext = this.getDraftContextForMode(mode);
		if (draftContext &&
			Array.isArray(draftContext.targetPath) &&
			draftContext.targetPath.length > 0) {
			return {
				targetPath: draftContext.targetPath,
				targetReadOnly: draftContext.targetReadOnly === true
			};
		}

		const focusedTarget = this.props.aiAssistantFocusedTarget || state.get(["appState", "aiAssistantFocusedTarget"]);
		if (focusedTarget &&
			Array.isArray(focusedTarget.targetPath) &&
			focusedTarget.targetPath.length > 0) {
			return {
				targetPath: focusedTarget.targetPath,
				targetReadOnly: focusedTarget.targetReadOnly === true
			};
		}

		return null;
	}

	getWritableFocusedInputTarget() {
		if (typeof document === "undefined") {
			return null;
		}

		if (this.isWritableExternalInputElement(this.lastFocusedExternalEditableElement)) {
			return this.lastFocusedExternalEditableElement;
		}

		if (this.isWritableExternalInputElement(document.activeElement)) {
			this.lastFocusedExternalEditableElement = document.activeElement;
			return document.activeElement;
		}

		return null;
	}

	canApplyDraftContent() {
		if (this.state.isWaitingForResponse) {
			return false;
		}
		if ((this.props.aiAssistantResponseLast || "").trim() === "") {
			return false;
		}
		const focusedInputTarget = this.getWritableFocusedInputTarget();
		if (this.preferFocusedInputForDraftApply === true) {
			return !!focusedInputTarget;
		}
		if (focusedInputTarget) {
			return true;
		}
		const targetContext = this.resolveDraftApplyTargetContext();
		return !!(
			targetContext &&
			Array.isArray(targetContext.targetPath) &&
			targetContext.targetPath.length > 0 &&
			!targetContext.targetReadOnly
		);
	}

	applyDraftContentToFocusedInput(content, { replace = false } = {}) {
		const target = this.getWritableFocusedInputTarget();
		if (!target) {
			return false;
		}
		const tag = target.tagName ? target.tagName.toUpperCase() : "";

		const insertContent = typeof content === "string" ? content : "";
		const currentValue = typeof target.value === "string" ? target.value : "";
		const start = typeof target.selectionStart === "number" ? target.selectionStart : currentValue.length;
		const end = typeof target.selectionEnd === "number" ? target.selectionEnd : currentValue.length;
		const nextValue = replace
			? insertContent
			: `${currentValue.slice(0, start)}${insertContent}${currentValue.slice(end)}`;

		const prototype = tag === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
		const valueSetterDescriptor = Object.getOwnPropertyDescriptor(prototype, "value");
		if (valueSetterDescriptor && typeof valueSetterDescriptor.set === "function") {
			valueSetterDescriptor.set.call(target, nextValue);
		} else {
			target.value = nextValue;
		}

		const nextCursorPos = replace ? nextValue.length : start + insertContent.length;
		if (typeof target.setSelectionRange === "function") {
			target.setSelectionRange(nextCursorPos, nextCursorPos);
		}
		target.dispatchEvent(new Event("input", { bubbles: true }));
		target.dispatchEvent(new Event("change", { bubbles: true }));
		this.bumpEditorRefreshCounter();
		return true;
	}

	getPreferredDraftContentFromResponse(responseText = "") {
		const blocks = this.getResponseBlocks(responseText);
		if (!Array.isArray(blocks) || blocks.length === 0) {
			return "";
		}

		const candidateBlock = blocks.find((block) => {
			const content = block && typeof block.content === "string" ? block.content.trim() : "";
			if (!content) {
				return false;
			}
			if (/^#{1,6}\s+/m.test(content) && content.split(/\n/).length === 1) {
				return false;
			}
			return content.length > 4;
		});
		return candidateBlock && candidateBlock.content ? candidateBlock.content.trim() : "";
	}

	applyDraftContentToTarget(content, { replace = false } = {}) {
		const trimmedContent = typeof content === "string" ? content.trim() : "";
		if (!trimmedContent) {
			this.appendAssistantErrorMessage("No draft content was found in the latest assistant response.");
			return;
		}

		if (this.preferFocusedInputForDraftApply === true) {
			if (this.applyDraftContentToFocusedInput(trimmedContent, { replace })) {
				return;
			}
			this.appendAssistantErrorMessage("No focused input target is available. Click into a field and try Insert/Replace again.");
			return;
		}

		const targetContext = this.resolveDraftApplyTargetContext();
		if (targetContext && Array.isArray(targetContext.targetPath) && targetContext.targetPath.length > 0) {
			if (targetContext.targetReadOnly) {
				this.appendAssistantErrorMessage("This target is read-only and cannot be updated.");
				return;
			}

			const currentValueRaw = state.get(targetContext.targetPath);
			const currentValue = typeof currentValueRaw === "string" ? currentValueRaw : "";
			const nextValue = replace
				? trimmedContent
				: `${currentValue}${currentValue ? "\n" : ""}${trimmedContent}`;

			this.context.tree.set(targetContext.targetPath, nextValue);
			this.bumpEditorRefreshCounter();
			return;
		}

		if (this.applyDraftContentToFocusedInput(trimmedContent, { replace })) {
			return;
		}

		if (!targetContext || !Array.isArray(targetContext.targetPath) || targetContext.targetPath.length === 0) {
			this.appendAssistantErrorMessage("No draft target is selected. Launch from a field to enable insert actions.");
			return;
		}
	}

	insertDraftFromAssistant({ replace = false } = {}) {
		const mode = this.state.selectedActivityOrFiles;
		if (!this.isDraftMode(mode)) {
			return;
		}
		const aiResponse = this.props.aiAssistantResponseLast || "";
		const draftContent = this.getPreferredDraftContentFromResponse(aiResponse);
		this.applyDraftContentToTarget(draftContent, { replace });
	}

	insertPromptFromFocusedInput({ replace = false } = {}) {
		const sourceTarget = this.getFocusedExternalEditableSource();
		if (!sourceTarget) {
			return;
		}
		const sourceValue = typeof sourceTarget.value === "string" ? sourceTarget.value : "";
		if (!replace && (sourceValue || "").trim() === "") {
			return;
		}

		const currentPrompt = typeof this.state.inputAiMessage === "string" ? this.state.inputAiMessage : "";
		const nextPrompt = replace
			? sourceValue
			: `${currentPrompt}${currentPrompt && !currentPrompt.endsWith("\n") ? "\n" : ""}${sourceValue}`;
		this.preferFocusedInputForDraftApply = true;

		this.setState({
			inputAiMessage: nextPrompt
		}, () => {
			const promptEl = this.refs.inputAiMessage;
			if (promptEl && typeof promptEl.focus === "function") {
				promptEl.focus();
				if (typeof promptEl.setSelectionRange === "function") {
					const endPos = nextPrompt.length;
					promptEl.setSelectionRange(endPos, endPos);
				}
			}
		});
	}

	appendAssistantErrorMessage(message) {
		let newData = this.context.tree.get(["appState", "aiAssistantResponse"]);
		newData = newData + `\n\n<b style='color: #ff8c8c'>${message}</b>\n\n`;
		this.context.tree.set(["appState", "aiAssistantResponse"], newData);

		let newDataLast = this.context.tree.get(["appState", "aiAssistantResponseLast"]);
		newDataLast = newDataLast + message;
		this.context.tree.set(["appState", "aiAssistantResponseLast"], newDataLast);
	}

	appendConversationHeader(message, mode = "") {
		let newData = this.context.tree.get(["appState", "aiAssistantResponse"]);
		newData = newData + `\n\n---\n\n**You:** ${message}\n\n---\n\n**Assistant:**\n\n`;

		this.context.tree.set(["appState", "aiAssistantResponse"], newData);
		this.context.tree.set(["appState", "aiAssistantResponseLast"], "");
		if (mode) {
			this.setState({
				lastResponseMode: mode
			});
		}
	}

	clearWaitingTimers() {
		if (this.responseSettleTimeoutId) {
			clearTimeout(this.responseSettleTimeoutId);
			this.responseSettleTimeoutId = null;
		}
		if (this.responseMaxWaitTimeoutId) {
			clearTimeout(this.responseMaxWaitTimeoutId);
			this.responseMaxWaitTimeoutId = null;
		}
	}

	startWaitingForResponse() {
		this.clearWaitingTimers();
		this.setState({
			isWaitingForResponse: true,
			hasReceivedResponseChunk: false
		});

		this.responseMaxWaitTimeoutId = setTimeout(() => {
			this.setState({
				isWaitingForResponse: false
			});
			this.responseMaxWaitTimeoutId = null;
		}, MAX_WAIT_FOR_RESPONSE_MS);
	}

	scheduleStopWaitingForResponse() {
		if (this.responseSettleTimeoutId) {
			clearTimeout(this.responseSettleTimeoutId);
		}

		this.responseSettleTimeoutId = setTimeout(() => {
			this.setState({
				isWaitingForResponse: false
			});
			this.responseSettleTimeoutId = null;
			if (this.responseMaxWaitTimeoutId) {
				clearTimeout(this.responseMaxWaitTimeoutId);
				this.responseMaxWaitTimeoutId = null;
			}
		}, STREAM_SETTLE_DELAY_MS);
	}

	stopWaitingForResponse() {
		this.clearWaitingTimers();
		this.setState({
			isWaitingForResponse: false
		});
	}

	getContextGeneratedAtLabel() {
		const generatedAt = this.authoringContextIndex &&
			this.authoringContextIndex.meta &&
			this.authoringContextIndex.meta.generatedAtIso
			? this.authoringContextIndex.meta.generatedAtIso
			: "";
		if (!generatedAt) {
			return "";
		}

		try {
			return new Date(generatedAt).toLocaleString();
		} catch (_err) {
			return generatedAt;
		}
	}

	getAuthoringContextTokenSet(message) {
		const tokens = ((message || "").toLowerCase().match(/[a-z0-9_/-]{2,}/g) || []);
		return new Set(tokens);
	}

	tokenSetIncludes(tokenSet, candidates) {
		for (const token of tokenSet) {
			for (const candidate of candidates) {
				if (token.indexOf(candidate) > -1 || candidate.indexOf(token) > -1) {
					return true;
				}
			}
		}
		return false;
	}

	cleanContextText(text, maxChars = 240) {
		if (!text || typeof text !== "string") {
			return "";
		}
		const cleaned = text.replace(/\s+/g, " ").trim();
		return cleaned.length > maxChars ? `${cleaned.slice(0, maxChars)}...` : cleaned;
	}

	pushPlainTextBlocks(rawText, blocks, blockIdRef) {
		if (!rawText || typeof rawText !== "string") {
			return;
		}

		const text = rawText
			.replace(/\r\n/g, "\n")
			.trim();
		if (!text) {
			return;
		}

		const candidateHeadingRegex = /^#{1,6}\s*candidate\b[^\n]*$/gim;
		const candidateMatches = [];
		let candidateMatch;
		while ((candidateMatch = candidateHeadingRegex.exec(text)) !== null) {
			candidateMatches.push({
				index: candidateMatch.index,
				text: candidateMatch[0] || ""
			});
		}
		if (candidateMatches.length > 0) {
			const firstMatchIdx = typeof candidateMatches[0].index === "number" ? candidateMatches[0].index : 0;
			const leadIn = text.slice(0, firstMatchIdx).trim();
			if (leadIn) {
				blocks.push({
					id: `b${blockIdRef.value++}`,
					type: "text",
					title: `Snippet ${blocks.length + 1}`,
					content: leadIn
				});
			}

			candidateMatches.forEach((candidateMatch, idx) => {
				const headingLine = (candidateMatch.text || "").replace(/^#{1,6}\s*/, "").trim();
				const headingStart = typeof candidateMatch.index === "number" ? candidateMatch.index : 0;
				const contentStart = headingStart + (candidateMatch.text || "").length;
				const nextMatch = candidateMatches[idx + 1];
				const contentEnd = nextMatch && typeof nextMatch.index === "number" ? nextMatch.index : text.length;
				const candidateContent = text.slice(contentStart, contentEnd).trim();
				if (!candidateContent) {
					return;
				}

				blocks.push({
					id: `b${blockIdRef.value++}`,
					type: "candidate",
					title: headingLine || `Candidate ${idx + 1}`,
					content: candidateContent
				});
			});
			return;
		}

		const paragraphs = text
			.split(/\n{2,}/)
			.map((chunk) => chunk.trim())
			.filter(Boolean);

		paragraphs.forEach((paragraph) => {
			blocks.push({
				id: `b${blockIdRef.value++}`,
				type: "text",
				title: `Snippet ${blocks.length + 1}`,
				content: paragraph
			});
		});
	}

	pushTextAndBulletBlocks(rawText, blocks, blockIdRef) {
		if (!rawText || typeof rawText !== "string") {
			return;
		}
		const text = rawText.replace(/\r\n/g, "\n");
		if (!text.trim()) {
			return;
		}

		const bulletGroupRegex = /((?:^|\n)\s*(?:[-*]|\d+\.)\s+[^\n]+(?:\n\s*(?:[-*]|\d+\.)\s+[^\n]+)+)/g;
		let cursor = 0;
		let match = bulletGroupRegex.exec(text);
		while (match) {
			const start = match.index;
			const end = start + match[0].length;
			const before = text.slice(cursor, start);
			this.pushPlainTextBlocks(before, blocks, blockIdRef);

			const bulletContent = match[0].trim();
			if (bulletContent) {
				blocks.push({
					id: `b${blockIdRef.value++}`,
					type: "bullets",
					title: `Bullet List ${blocks.length + 1}`,
					content: bulletContent
				});
			}

			cursor = end;
			match = bulletGroupRegex.exec(text);
		}

		const tail = text.slice(cursor);
		this.pushPlainTextBlocks(tail, blocks, blockIdRef);
	}

	getResponseBlocks(responseText) {
		if (!responseText || typeof responseText !== "string") {
			return [];
		}
		const normalized = responseText.replace(/\r\n/g, "\n").trim();
		if (!normalized) {
			return [];
		}

		const blocks = [];
		const blockIdRef = { value: 1 };
		const codeFenceRegex = /```([a-zA-Z0-9_-]+)?\s*\n([\s\S]*?)```/g;
		let cursor = 0;
		let match = codeFenceRegex.exec(normalized);
		while (match) {
			const start = match.index;
			const end = start + match[0].length;
			const before = normalized.slice(cursor, start);
			this.pushTextAndBulletBlocks(before, blocks, blockIdRef);

			const language = (match[1] || "").trim().toLowerCase();
			const code = (match[2] || "").trim();
			if (code) {
				blocks.push({
					id: `b${blockIdRef.value++}`,
					type: "code",
					title: language ? `Code (${language})` : `Code ${blocks.length + 1}`,
					content: code,
					language
				});
			}

			cursor = end;
			match = codeFenceRegex.exec(normalized);
		}

		const tail = normalized.slice(cursor);
		this.pushTextAndBulletBlocks(tail, blocks, blockIdRef);

		if (blocks.length === 0) {
			return [{
				id: "b1",
				type: "text",
				title: "Snippet 1",
				content: normalized
			}];
		}

		return blocks;
	}

	renderResponseSnippetContent(block) {
		const content = (block && block.content) ? block.content : "";
		const preview = content.length > MAX_RESPONSE_SNIPPET_PREVIEW_CHARS
			? `${content.slice(0, MAX_RESPONSE_SNIPPET_PREVIEW_CHARS)}...`
			: content;

		if (block.type === "code") {
			return <pre style={{
				margin: 0,
				padding: "10px",
				whiteSpace: "pre-wrap",
				wordBreak: "break-word",
				backgroundColor: "#0a1220",
				border: "1px solid #253a55",
				borderRadius: "8px",
				color: "#dbe8fa",
				fontSize: "12px",
				lineHeight: 1.45
			}}>{preview}</pre>;
		}

		return <div style={{
			whiteSpace: "pre-wrap",
			wordBreak: "break-word",
			color: "#dce8f8",
			fontSize: "13px",
			lineHeight: 1.45
		}}>{preview}</div>;
	}

	getResponseSnippetBlocks() {
		const selectedMode = this.state.selectedActivityOrFiles;
		const responseMode = this.state.lastResponseMode || selectedMode;
		const shouldShowSnippets =
			this.isDraftMode(selectedMode) ||
			this.isDraftMode(responseMode);
		if (!shouldShowSnippets) {
			return [];
		}

		const latestResponse = this.props.aiAssistantResponseLast || "";
		const blocks = this.getResponseBlocks(latestResponse);
		if (!Array.isArray(blocks) || blocks.length === 0) {
			return [];
		}

		return blocks;
	}

	renderResponseSnippets(blocks = []) {
		if (!Array.isArray(blocks) || blocks.length === 0) {
			return null;
		}

		const selectedMode = this.state.selectedActivityOrFiles;
		const canApplyDraftBlock = this.isDraftMode(selectedMode) && this.canApplyDraftContent();

		return <div style={{
			display: "flex",
			flexDirection: "column",
			gap: "8px"
		}}>
			<div style={{ color: "#bcd1ea", fontWeight: 600, marginBottom: "2px" }}>Response Snippets</div>
			{blocks.map((block) => (
				<div key={block.id} style={{
					border: "1px solid #263a53",
					borderRadius: "8px",
					padding: "8px",
					backgroundColor: "#101b2d"
				}}>
					<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: "6px", gap: "8px" }}>
						<div style={{ color: "#9fc2e6", fontWeight: 600, fontSize: "12px" }}>{block.title}</div>
						<div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginLeft: "auto" }}>
							{canApplyDraftBlock &&
								<>
									<Button
										bsStyle="primary"
										style={{ borderRadius: "7px", backgroundColor: "#255ca1", borderColor: "#3069af", padding: "4px 8px", fontSize: "12px" }}
										onClick={() => this.applyDraftContentToTarget(block.content, { replace: false })}
									>
										Insert
									</Button>
									<Button
										bsStyle="primary"
										style={{ borderRadius: "7px", backgroundColor: "#1d4f8a", borderColor: "#2a629e", padding: "4px 8px", fontSize: "12px" }}
										onClick={() => this.applyDraftContentToTarget(block.content, { replace: true })}
									>
										Replace
									</Button>
								</>
							}
							<Button
								bsStyle="primary"
								style={{ borderRadius: "7px", backgroundColor: "#1f4d88", borderColor: "#2f68ab", padding: "4px 8px", fontSize: "12px" }}
								onClick={() => copyToClipboard(block.content)}
							>
								<FontAwesomeIcon icon="copy" /> Copy
							</Button>
						</div>
					</div>
					{this.renderResponseSnippetContent(block)}
				</div>
			))}
		</div>;
	}

	getMatchedPropertyDocs(index, tokenSet, message) {
		const byScope = (index && index.propertyDocs && index.propertyDocs.byScope) ? index.propertyDocs.byScope : {};
		const docs = [];

		Object.keys(byScope).forEach((scope) => {
			const scopedProperties = (byScope[scope] && Array.isArray(byScope[scope].properties)) ? byScope[scope].properties : [];
			scopedProperties.forEach((doc) => {
				docs.push({
					scope,
					key: doc.key || "",
					name: doc.name || "",
					description: doc.description || ""
				});
			});
		});

		const question = (message || "").toLowerCase();
		const scored = docs.map((doc) => {
			let score = 0;
			const key = (doc.key || "").toLowerCase();
			const name = (doc.name || "").toLowerCase();
			const description = (doc.description || "").toLowerCase();

			if (key && question.indexOf(key) > -1) {
				score += 12;
			}
			if (name && question.indexOf(name) > -1) {
				score += 7;
			}

			for (const token of tokenSet) {
				if (token.length < 3) {
					continue;
				}
				if (key === token || name === token) {
					score += 6;
				} else if (key.indexOf(token) > -1 || name.indexOf(token) > -1) {
					score += 4;
				}

				if (description.indexOf(token) > -1) {
					score += 1;
				}
			}

			return { ...doc, score };
		})
			.filter((doc) => doc.score > 0)
			.sort((a, b) => {
				if (b.score !== a.score) {
					return b.score - a.score;
				}
				if (a.scope !== b.scope) {
					return a.scope.localeCompare(b.scope);
				}
				return a.key.localeCompare(b.key);
			});

		return scored.slice(0, MAX_AUTHORING_MATCHED_PROPERTIES);
	}

	buildAuthoringContextMessage(userMessage) {
		const message = userMessage || "";

		if (!this.authoringContextIndex) {
			const summary = this.cleanContextText(this.authoringContextSummary, 8000);
			return [
				"You are the AmbiFi Authoring Help Assistant.",
				"Use the provided context to answer questions about authoring capabilities and runtime behavior.",
				"From an author perspective, treat the player-checklist JSON model as canonical.",
				summary ? `Context Summary:\n${summary}` : "",
				`Author question: ${message}`
			].filter(Boolean).join("\n\n");
		}

		const index = this.authoringContextIndex;
		const tokenSet = this.getAuthoringContextTokenSet(message);
		const includeRuntime = this.tokenSetIncludes(tokenSet, ["runtime", "api", "web", "mobile", "player", "method", "function", "script"]);
		const includeProperties = this.tokenSetIncludes(tokenSet, ["property", "properties", "field", "fields", "schema", "option", "setting", "label", "author", "content"]);
		const includeToolbar = this.tokenSetIncludes(tokenSet, ["toolbar", "menu", "nav", "navigation", "button", "layout", "panel", "screen"]);

		const sections = [];
		sections.push([
			"You are the AmbiFi Authoring Help Assistant.",
			"Answer with practical, accurate guidance for AmbiFi authors.",
			"Important: from an author perspective, the canonical model is player-checklist JSON. Avoid internal authoring-memory terminology unless explicitly asked."
		].join("\n"));

		const contextMeta = index.meta || {};
		const runtimeParity = index.runtimeApiParity || {};
		const transform = index.transform || {};
		const authoring = index.authoring || {};
		const models = index.models || {};
		const playerSchema = models.schema && models.schema.playerChecklist ? models.schema.playerChecklist : {};
		const overrides = index.overrides || {};
		const overrideDirectives = Array.isArray(overrides.authoringPromptDirectives) ? overrides.authoringPromptDirectives : [];
		const overrideHierarchyRules = Array.isArray(overrides.authoringHierarchyRules) ? overrides.authoringHierarchyRules : [];
		const overrideCorrections = Array.isArray(overrides.knownCorrections) ? overrides.knownCorrections : [];

		if (overrideDirectives.length > 0 || overrideHierarchyRules.length > 0 || overrideCorrections.length > 0) {
			const overrideLines = ["Authoritative manual overrides (follow these rules over inferred guidance):"];

			if (overrideDirectives.length > 0) {
				overrideLines.push("- Prompt directives:");
				overrideDirectives.slice(0, 20).forEach((directive) => {
					overrideLines.push(`  - ${this.cleanContextText(directive, 240)}`);
				});
			}

			if (overrideHierarchyRules.length > 0) {
				overrideLines.push("- Authoring hierarchy rules:");
				overrideHierarchyRules.slice(0, 20).forEach((rule) => {
					overrideLines.push(`  - ${this.cleanContextText(rule, 220)}`);
				});
			}

			if (overrideCorrections.length > 0) {
				overrideLines.push("- Known corrections:");
				overrideCorrections.slice(0, 20).forEach((correction) => {
					const topic = correction.topic ? `${this.cleanContextText(correction.topic, 80)}: ` : "";
					const correct = this.cleanContextText(correction.correct || "", 220);
					if (correct) {
						overrideLines.push(`  - ${topic}${correct}`);
					}
				});
			}

			sections.push(overrideLines.join("\n"));
		}

		sections.push([
			"Authoring Context Snapshot:",
			`- Generated: ${contextMeta.generatedAtIso || "unknown"}`,
			`- Fingerprint: ${contextMeta.sourceFingerprint || "unknown"}`,
			`- Runtime parity web/mobile: ${runtimeParity.parityPercent || 0}% (${runtimeParity.commonMethodCount || 0} common methods)`,
			`- Item types: ${Array.isArray(authoring.itemTypes) ? authoring.itemTypes.map((item) => `${item.name} (${item.type})`).join(", ") : ""}`,
			`- Link action types: ${Array.isArray(authoring.linkActionTypes) ? authoring.linkActionTypes.join(", ") : ""}`,
			`- Transform functions: ${Array.isArray(transform.exportedFunctions) ? transform.exportedFunctions.join(", ") : ""}`
		].join("\n"));

		if (Array.isArray(playerSchema.topLevelProperties) && playerSchema.topLevelProperties.length > 0) {
			sections.push([
				"Player checklist top-level fields:",
				playerSchema.topLevelProperties.join(", ")
			].join("\n"));
		}

		if (includeProperties) {
			const matches = this.getMatchedPropertyDocs(index, tokenSet, message);
			if (matches.length > 0) {
				sections.push([
					"Most relevant documented properties:",
					...matches.map((doc) => `- ${doc.scope}.${doc.key}: ${this.cleanContextText(doc.description || doc.name, 240)}`)
				].join("\n"));
			}
		}

		if (includeToolbar) {
			const toolbarLabels = Array.isArray(authoring.toolbarLabels) ? authoring.toolbarLabels : [];
			const navRoutes = Array.isArray(authoring.navRoutes) ? authoring.navRoutes : [];
			sections.push([
				"UI capability surfaces:",
				`- Toolbar labels: ${toolbarLabels.join(", ")}`,
				`- Navigation routes: ${navRoutes.map((route) => `${route.label} (${route.route})`).join(", ")}`
			].join("\n"));
		}

		if (includeRuntime) {
			const commonMethods = Array.isArray(runtimeParity.commonMethods) ? runtimeParity.commonMethods.slice(0, MAX_AUTHORING_METHODS) : [];
			const webOnlyMethods = Array.isArray(runtimeParity.webOnlyMethods) ? runtimeParity.webOnlyMethods : [];
			const mobileOnlyMethods = Array.isArray(runtimeParity.mobileOnlyMethods) ? runtimeParity.mobileOnlyMethods : [];

			sections.push([
				"Runtime API details:",
				`- Common methods: ${commonMethods.join(", ")}`,
				`- Web-only methods: ${webOnlyMethods.join(", ") || "none"}`,
				`- Mobile-only methods: ${mobileOnlyMethods.join(", ") || "none"}`
			].join("\n"));
		}

		if (this.authoringContextSummary) {
			sections.push([
				"Generated summary excerpt:",
				this.cleanContextText(this.authoringContextSummary, 1800)
			].join("\n"));
		}

		const trimmedSections = [];
		let currentLength = 0;
		sections.forEach((section) => {
			if (!section) {
				return;
			}
			const nextLength = currentLength + section.length + 2;
			if (nextLength > MAX_AUTHORING_CONTEXT_CHARS) {
				return;
			}
			trimmedSections.push(section);
			currentLength = nextLength;
		});

		return `${trimmedSections.join("\n\n")}\n\nAuthor question: ${message}`;
	}

	async ensureAuthoringContextLoaded() {
		if (!this.isAuthoringModeEnabled()) {
			return false;
		}
		if (this.authoringContextIndex || this.authoringContextSummary) {
			return true;
		}
		if (this.authoringContextLoadPromise) {
			return this.authoringContextLoadPromise;
		}

		this.setState({
			authoringContextStatus: "loading",
			authoringContextError: ""
		});

		this.authoringContextLoadPromise = (async () => {
			try {
				const response = await fetch(AUTHORING_CONTEXT_INDEX_URL, {
					method: "GET",
					cache: "no-cache"
				});

				if (response.ok) {
					const json = await response.json();
					this.authoringContextIndex = json;
					this.setState({
						authoringContextStatus: "ready",
						authoringContextError: ""
					});
					return true;
				}
			} catch (_err) {
				// Fall through to summary fallback below.
			}

			try {
				const summaryResponse = await fetch(AUTHORING_CONTEXT_SUMMARY_URL, {
					method: "GET",
					cache: "no-cache"
				});

				if (summaryResponse.ok) {
					this.authoringContextSummary = await summaryResponse.text();
					this.setState({
						authoringContextStatus: "ready",
						authoringContextError: ""
					});
					return true;
				}
			} catch (_err) {
				// Continue to error state.
			}

			this.setState({
				authoringContextStatus: "error",
				authoringContextError: "Authoring context was not found at /assistant-context. Run the assistant-context generator to publish context artifacts."
			});
			return false;
		})();

		try {
			return await this.authoringContextLoadPromise;
		} finally {
			this.authoringContextLoadPromise = null;
		}
	}

	async createThreadIfNeeded() {
		if (this.state.threadId) {
			return this.state.threadId;
		}

		try {
			const responseCurrentThread = await fetch("https://api.ambifi.com/services/openai/threads", {
				method: "POST"
			});
			const jsonResponseCurrentThread = await responseCurrentThread.json();

			if (jsonResponseCurrentThread && jsonResponseCurrentThread.id) {
				this.setState({
					threadId: jsonResponseCurrentThread.id
				});
				return jsonResponseCurrentThread.id;
			}
		} catch (e) {
			console.error(e);
		}

		return "";
	}

	componentDidUpdate(prevProps, prevState) {
		let messageBody = document.getElementById("openai-markdown2");
		if (messageBody) {
			messageBody.scrollTop = 10000000;
			console.log("SCROLL", messageBody.scrollTop);
		}

		if (this.props.aiAssistantLaunchRequest &&
			(!prevProps.aiAssistantLaunchRequest ||
				this.props.aiAssistantLaunchRequest.requestId !== prevProps.aiAssistantLaunchRequest.requestId)) {
			this.handleLaunchRequest(this.props.aiAssistantLaunchRequest);
		}

		if (this.props.aiAssistantId !== prevProps.aiAssistantId && this.props.aiAssistantId && this.props.aiAssistantId !== "") {
			this.createThreadIfNeeded();
		}

		if ((this.state.selectedActivityOrFiles === "authoring" || this.isScriptMode(this.state.selectedActivityOrFiles) || this.isDraftMode(this.state.selectedActivityOrFiles)) &&
			prevState.selectedActivityOrFiles !== this.state.selectedActivityOrFiles) {
			this.ensureAuthoringContextLoaded();
		}

		if ((this.state.selectedActivityOrFiles === "authoring" || this.isScriptMode(this.state.selectedActivityOrFiles) || this.isDraftMode(this.state.selectedActivityOrFiles)) &&
			prevProps.aiAssistantId !== this.props.aiAssistantId) {
			this.ensureAuthoringContextLoaded();
		}

		const currentResponseLast = this.props.aiAssistantResponseLast || "";
		const previousResponseLast = prevProps.aiAssistantResponseLast || "";
		const hasNewResponseChunk = currentResponseLast.length > previousResponseLast.length &&
			currentResponseLast.indexOf(previousResponseLast) === 0;

		if (this.state.isWaitingForResponse && hasNewResponseChunk) {
			if (!this.state.hasReceivedResponseChunk) {
				this.setState({
					hasReceivedResponseChunk: true
				});
			}
			this.scheduleStopWaitingForResponse();
		}

		if (hasNewResponseChunk &&
			this.state.responseViewTab !== "snippets" &&
			this.isDraftMode(this.state.lastResponseMode)) {
			this.setState({
				responseViewTab: "snippets"
			});
		}

		this.ensureSelectedMode();
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (this.props.aiAssistantResponse !== nextProps.aiAssitantResponse) {
			let messageBody = document.getElementById("openai-markdown2");
			messageBody.scrollTop = 10000;

			console.log("SCROLL", messageBody.scrollTop);
		}
	}

	async componentDidMount() {
		if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
			document.addEventListener("focusin", this.handleDocumentFocusIn, true);
			if (this.isWritableExternalInputElement(document.activeElement)) {
				this.lastFocusedExternalEditableElement = document.activeElement;
			}
		}

		const element = document.getElementById("openai-markdown");
		const renderer = smd.default_renderer(element);
		parser = smd.parser(renderer);

		// let headers = new Headers();

		// headers.append('Authorization', 'Basic amVmZi5ib25hc3NvQG1pcmFsb3VhZXJvLmNvbTpTaXhGb3VyNzJHb2xm');

		const response = await fetch("https://api.ambifi.com/services/openai/files", {
			method: "GET",
			// headers: headers
		});
		const jsonResponse = await response.json();

		const files = jsonResponse.data.filter((file) => {
			return file.filename.startsWith(this.props.user.identityId + "/");
		});

		this.context.tree.set(["tree", "root", "entity", "aiAssistantFiles"], files);

		if (this.props.aiAssistantId && this.props.aiAssistantId !== "") {
			// TODO: will need to upgrade to use a vector store

			// const responseChecklistFiles = await fetch(`https://api.ambifi.com/services/openai/assistants/${this.props.aiAssistantId}/files`, {
			// 	method: "GET",
			// 	// headers: headers
			// });
			// const jsonResponseChecklistFiles = await responseChecklistFiles.json();
			// const checklistFiles = jsonResponseChecklistFiles.data.filter((file) => {
			// 	return file;
			// });

			// this.context.tree.set(["tree", "root", "entity", "aiAssistantChecklistFiles"], checklistFiles);

			// Create thread - probably need a way to clear everything too
			const responseCurrentThread = await fetch("https://api.ambifi.com/services/openai/threads", {
				method: "POST",
				// headers: headers
			});
			const jsonResponseCurrentThread = await responseCurrentThread.json();

			this.setState({
				threadId: jsonResponseCurrentThread.id
			});
		}

		if (this.isAuthoringModeEnabled()) {
			this.ensureAuthoringContextLoaded();
		}

		const availableModes = this.getAvailableModes();
		if (availableModes.includes("authoring") && this.state.selectedActivityOrFiles === "org") {
			this.setState({
				selectedActivityOrFiles: "authoring"
			});
		}

		if (this.props.aiAssistantLaunchRequest && this.props.aiAssistantLaunchRequest.requestId) {
			this.handleLaunchRequest(this.props.aiAssistantLaunchRequest);
		}

		this.ensureSelectedMode();
	}

	componentWillUnmount() {
		this.clearWaitingTimers();
		if (typeof document !== "undefined" && typeof document.removeEventListener === "function") {
			document.removeEventListener("focusin", this.handleDocumentFocusIn, true);
		}
	}

	getModeOptionLabel(mode) {
		if (mode === "authoring") {
			return "Ask questions about AmbiFi authoring capabilities.";
		}
		if (mode === DRAFT_ASSISTANT_MODE_FIELD_TEXT) {
			return "Create or rewrite content for a field (with insert actions).";
		}
		if (mode === SCRIPT_ASSISTANT_MODE_FUNCTIONS) {
			return "Script activity Functions (with insert actions).";
		}
		if (mode === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER) {
			return "Script selected Value Trigger (with insert actions).";
		}
		if (mode === "activity") {
			return "Share current activity content with the assistant.";
		}
		if (mode === "org") {
			return "Chat with all content shared in the org.";
		}
		if (mode === "files") {
			return "General chat and share selected files with the assistant.";
		}
		return "Select assistant mode.";
	}

	renderCollapsedModeSummary() {
		const mode = this.state.selectedActivityOrFiles;
		const label = this.getModeOptionLabel(mode);

		return <div style={{ color: "#dbe5f4" }}>
			<div style={{ fontWeight: 600, fontSize: "13px", lineHeight: 1.35 }}>{label}</div>
		</div>;
	}

	renderSelectActivityOrFiles() {
		const getOptionStyle = (isSelected) => {
			return {
				display: "block",
				padding: "10px 12px",
				marginTop: "8px",
				borderRadius: "8px",
				border: isSelected ? "1px solid #4d88d8" : "1px solid #2d3f57",
				backgroundColor: isSelected ? "#16263a" : "#101a29",
				color: isSelected ? "#f4f9ff" : "#dbe5f4",
				fontWeight: isSelected ? 600 : 500
			};
		};

		const availableModes = this.getAvailableModes();

		return <div style={{ display: "flex", flexDirection: "column" }}>
			{availableModes.includes("authoring") &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === "authoring")}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value="authoring"
						checked={this.state.selectedActivityOrFiles === "authoring"}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: "authoring"
							}, () => {
								this.ensureAuthoringContextLoaded();
							});
						}}
					/>
					Ask questions about AmbiFi authoring capabilities.
				</label>
			}
			{this.state.selectedActivityOrFiles === "authoring" && this.renderAuthoringContext()}
			{availableModes.includes(DRAFT_ASSISTANT_MODE_FIELD_TEXT) &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === DRAFT_ASSISTANT_MODE_FIELD_TEXT)}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value={DRAFT_ASSISTANT_MODE_FIELD_TEXT}
						checked={this.state.selectedActivityOrFiles === DRAFT_ASSISTANT_MODE_FIELD_TEXT}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: DRAFT_ASSISTANT_MODE_FIELD_TEXT
							}, () => {
								this.ensureAuthoringContextLoaded();
							});
						}}
					/>
					Create or rewrite content for a field (with insert actions).
				</label>
			}
			{this.state.selectedActivityOrFiles === DRAFT_ASSISTANT_MODE_FIELD_TEXT && this.renderDraftContext(DRAFT_ASSISTANT_MODE_FIELD_TEXT)}
			{availableModes.includes(SCRIPT_ASSISTANT_MODE_FUNCTIONS) &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === SCRIPT_ASSISTANT_MODE_FUNCTIONS)}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value={SCRIPT_ASSISTANT_MODE_FUNCTIONS}
						checked={this.state.selectedActivityOrFiles === SCRIPT_ASSISTANT_MODE_FUNCTIONS}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: SCRIPT_ASSISTANT_MODE_FUNCTIONS
							}, () => {
								this.ensureAuthoringContextLoaded();
							});
						}}
					/>
					Script activity Functions (with insert actions).
				</label>
			}
			{this.state.selectedActivityOrFiles === SCRIPT_ASSISTANT_MODE_FUNCTIONS && this.renderScriptContext(SCRIPT_ASSISTANT_MODE_FUNCTIONS)}
			{availableModes.includes(SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER) &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER)}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value={SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER}
						checked={this.state.selectedActivityOrFiles === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER
							}, () => {
								this.ensureAuthoringContextLoaded();
							});
						}}
					/>
					Script selected Value Trigger (with insert actions).
				</label>
			}
			{this.state.selectedActivityOrFiles === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER && this.renderScriptContext(SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER)}
			{availableModes.includes("activity") &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === "activity")}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value="activity"
						checked={this.state.selectedActivityOrFiles === "activity"}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: "activity"
							});
						}}
					/>
					Share current activity content with the assistant.
				</label>
			}
			{this.state.selectedActivityOrFiles === "activity" && this.renderCurrentActivity()}
			{availableModes.includes("org") &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === "org")}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value="org"
						checked={this.state.selectedActivityOrFiles === "org"}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: "org"
							});
						}}
					/>
					Chat with all content shared in the org.
				</label>
			}
			{this.state.selectedActivityOrFiles === "org" && this.renderOrg()}
			{availableModes.includes("files") &&
				<label style={getOptionStyle(this.state.selectedActivityOrFiles === "files")}>
					<input
						style={{ marginRight: "10px" }}
						type="radio"
						value="files"
						checked={this.state.selectedActivityOrFiles === "files"}
						onChange={() => {
							this.setState({
								selectedActivityOrFiles: "files"
							});
						}}
					/>
					General chat and share selected files with the assistant.
				</label>
			}
			{this.state.selectedActivityOrFiles === "files" && this.renderFiles()}
		</div>;
	}

	renderAuthoringContext() {
		if (this.state.selectedActivityOrFiles !== "authoring") {
			return null;
		}

		const generatedAtLabel = this.getContextGeneratedAtLabel();

		return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
			<div><i>Ask how to author activities, configure properties, and use runtime APIs across web and mobile players. This mode uses generated AmbiFi authoring context.</i></div>
			{this.state.authoringContextStatus === "loading" &&
				<div style={{ marginTop: "6px" }}><i>Loading authoring context...</i></div>
			}
			{this.state.authoringContextStatus === "ready" && generatedAtLabel &&
				<div style={{ marginTop: "6px" }}><i>Context generated: {generatedAtLabel}</i></div>
			}
			{this.state.authoringContextStatus === "error" &&
				<div style={{ marginTop: "6px", color: "#ff9f9f" }}><i>{this.state.authoringContextError}</i></div>
			}
		</div>;
	}

	renderDraftContext(mode) {
		if (this.state.selectedActivityOrFiles !== mode) {
			return null;
		}

		const draftContext = this.getDraftContextForMode(mode);
		const generatedAtLabel = this.getContextGeneratedAtLabel();

		return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
			<div><i>Generate concise authoring text candidates and use Insert/Replace actions to apply the response.</i></div>
			{draftContext && draftContext.targetReadOnly &&
				<div style={{ marginTop: "6px", color: "#ff9f9f" }}><i>This target is read-only. You can ask questions, but insert actions are disabled.</i></div>
			}
			{this.state.authoringContextStatus === "loading" &&
				<div style={{ marginTop: "6px" }}><i>Loading authoring context...</i></div>
			}
			{this.state.authoringContextStatus === "ready" && generatedAtLabel &&
				<div style={{ marginTop: "6px" }}><i>Context generated: {generatedAtLabel}</i></div>
			}
			{this.state.authoringContextStatus === "error" &&
				<div style={{ marginTop: "6px", color: "#ff9f9f" }}><i>{this.state.authoringContextError}</i></div>
			}
		</div>;
	}

	renderScriptContext(mode) {
		if (this.state.selectedActivityOrFiles !== mode) {
			return null;
		}

		const scriptContext = this.getScriptContextForMode(mode);
		const targetLabel = scriptContext && scriptContext.targetLabel ? scriptContext.targetLabel : "";
		const generatedAtLabel = this.getContextGeneratedAtLabel();

		return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
			<div><i>Generate AmbiFi JavaScript using shared authoring/runtime context, then insert or replace directly in the target script.</i></div>
			{targetLabel &&
				<div style={{ marginTop: "6px" }}><i>Target: {targetLabel}</i></div>
			}
			{scriptContext && scriptContext.targetReadOnly &&
				<div style={{ marginTop: "6px", color: "#ff9f9f" }}><i>This target is read-only. You can ask questions, but insert actions are disabled.</i></div>
			}
			{this.state.authoringContextStatus === "loading" &&
				<div style={{ marginTop: "6px" }}><i>Loading authoring context...</i></div>
			}
			{this.state.authoringContextStatus === "ready" && generatedAtLabel &&
				<div style={{ marginTop: "6px" }}><i>Context generated: {generatedAtLabel}</i></div>
			}
			{this.state.authoringContextStatus === "error" &&
				<div style={{ marginTop: "6px", color: "#ff9f9f" }}><i>{this.state.authoringContextError}</i></div>
			}
		</div>;
	}

	renderOrg() {
		if (this.state.selectedActivityOrFiles !== "org") {
			return null;
		}

		return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
			<div><i>This will allow you to ask questions about all of the content shared in your organization that you have access to.</i></div>
		</div>;
	}

	renderCurrentActivity() {
		if (this.state.selectedActivityOrFiles !== "activity") {
			return null;
		}

		const hasActivity = this.getCurrentActivityId() !== "";

		if (hasActivity) {
			return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
				<div><i>The content of this activity will be shared with your assistant. This will update content directly in your activity.</i></div>
			</div>;
		}

		return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
			<div><i>Activity context is not available on this screen.</i></div>
		</div>;
	}

	renderFiles() {
		if (this.state.selectedActivityOrFiles !== "files") {
			return null;
		}

		const files = Array.isArray(this.props.files) ? this.props.files : (Array.isArray(this.props.rootAssistantFiles) ? this.props.rootAssistantFiles : []);
		const checklistFiles = Array.isArray(this.props.checklistFiles) ? this.props.checklistFiles : (Array.isArray(this.props.rootAssistantChecklistFiles) ? this.props.rootAssistantChecklistFiles : []);

		let fileRows = files.map((item) => {
			// Need to determine if checked based on a parallel data structure looking at files enabled in this assistant

			let fileChecked = false;
			if (checklistFiles.find((newItem) => {
				return newItem === item.id;
			})) {
				fileChecked = true;
			}

			return <tr style={{ borderStyle: "none" }} key={item.id}>
				<td style={{ borderStyle: "none", width: "50%", wordBreak: "break-all" }}>
					<Checkbox checked={fileChecked} onChange={(e) => {
						if (e.target.checked) {
							// Add to Assistant Files
							assistantsFilesCreate(this.props.aiAssistantId, item.id);
						} else {
							// Remove from Assistant Files
							assistantsFilesDelete(this.props.aiAssistantId, item.id);
						}
					}}>
						{item.filename.replace(this.props.user.identityId + "/", "")}
					</Checkbox>
				</td>
				<td style={{ borderStyle: "none", width: "25%" }}>
					{new Date(item.created_at * 1000).toLocaleString()}
				</td>
				<td style={{ borderStyle: "none", width: "25%" }}>
					<div style={{ display: "flex" }}>
						{/* <Button style={{color: "white", backgroundColor: "#0E7AFE"}} onClick={() => {
							// getContent(this.props.aiAssistantId, item.id);

							getContentStreamed(smd, parser, item.id, this.props.aiAssistantId, );
						}}>
							Get Content
						</Button>	 */}
						<Button style={{ color: "white", backgroundColor: "red" }} onClick={() => {
							filesDelete(item.id);
						}}>
							Delete
						</Button>
					</div>
				</td>
			</tr>;
		});

		if (fileRows.length === 0) {
			return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}><i>You currently do not have any files uploaded.</i></div>;
		} else {
			const filesComponent = <div style={{ maxHeight: "200px", overflow: "auto" }}><table style={{ borderStyle: "none" }}>
				<thead>
					<tr style={{ borderStyle: "none" }}>
						<th style={{ borderStyle: "none", width: "50%" }} scope="col">Filename</th>
						<th style={{ borderStyle: "none", width: "25%" }} scope="col">Created At</th>
						<th style={{ borderStyle: "none", width: "25%" }} scope="col">Actions</th>
					</tr>
				</thead>
				<tbody>
					{fileRows}
				</tbody>
			</table></div>;

			return <div style={{ marginLeft: "32px", marginTop: "8px", color: "#a8b8cd" }}>
				<div><i>Enable any files below that you want to share with your AI Assistant</i></div>
				{filesComponent}
			</div>;
		}
	}

	render() {
		// this.renderCurrentActivity();
		// this.renderFiles();
		const currentActivityId = this.getCurrentActivityId();
		const availableModes = this.getAvailableModes();
		const selectedScriptContext = this.isScriptMode(this.state.selectedActivityOrFiles)
			? this.getScriptContextForMode(this.state.selectedActivityOrFiles)
			: null;
		const latestScriptCode = this.isScriptMode(this.state.selectedActivityOrFiles)
			? extractCodeFromAssistantResponse(this.props.aiAssistantResponseLast || "")
			: "";
		const canInsertScript = !!(
			selectedScriptContext &&
			Array.isArray(selectedScriptContext.targetPath) &&
			!selectedScriptContext.targetReadOnly &&
			latestScriptCode.trim() !== "" &&
			!this.state.isWaitingForResponse
		);
		const canInsertDraft = this.isDraftMode(this.state.selectedActivityOrFiles) && this.canApplyDraftContent();
		const focusedEditableSourceForPrompt = this.getFocusedExternalEditableSource();
		const canInsertPromptFromField = this.isDraftMode(this.state.selectedActivityOrFiles) &&
			!this.state.isWaitingForResponse &&
			!!focusedEditableSourceForPrompt;
		const snippetBlocks = this.getResponseSnippetBlocks();
		const hasSnippetTab = Array.isArray(snippetBlocks) && snippetBlocks.length > 0;
		const responseViewTab = hasSnippetTab ? this.state.responseViewTab : "response";
		const modePanelCollapsed = this.state.modePanelCollapsed === true;
		const getTabButtonStyle = (isActive) => ({
			borderRadius: "7px",
			padding: "4px 10px",
			fontSize: "12px",
			fontWeight: 600,
			backgroundColor: isActive ? "#2b7de9" : "#1a2d46",
			borderColor: isActive ? "#2b7de9" : "#2f4d73",
			color: "#ffffff"
		});
		const responseActionButtonStyle = {
			borderRadius: "7px",
			padding: "4px 10px",
			fontSize: "12px"
		};

		return (
			// <div style={{ width: "100%", height: "100%" }}>
			<div ref={(node) => { this.assistantRootEl = node; }} style={{ display: "flex", flexDirection: "column", height: "100%", gap: "10px" }}>
				<div style={{
					flex: "0 0 auto",
					flexShrink: 0,
					minHeight: modePanelCollapsed ? "58px" : "190px",
					maxHeight: modePanelCollapsed ? "92px" : "42%",
					overflow: "hidden",
					border: "1px solid #2d3f57",
					borderRadius: "10px",
					backgroundColor: "#0f1827",
					display: "flex",
					flexDirection: "column"
				}}>
					<div style={{
						flex: "0 0 auto",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "8px 10px",
						borderBottom: modePanelCollapsed ? "none" : "1px solid #2d3f57",
						backgroundColor: "#101a2b"
					}}>
						<div style={{ color: "#bcd1ea", fontWeight: 600, fontSize: "13px" }}>Assistant Mode</div>
						<Button
							bsStyle="primary"
							style={{
								borderRadius: "7px",
								padding: "2px 8px",
								backgroundColor: "#1a2d46",
								borderColor: "#2f4d73",
								color: "#ffffff"
							}}
							onClick={() => this.setState({ modePanelCollapsed: !modePanelCollapsed })}
						>
							<FontAwesomeIcon icon={modePanelCollapsed ? "chevron-down" : "chevron-up"} />
						</Button>
					</div>

					{modePanelCollapsed
						? <div style={{ flex: 1, minHeight: 0, padding: "10px 12px" }}>
							{this.renderCollapsedModeSummary()}
						</div>
						: <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "10px 12px" }}>
							{this.renderSelectActivityOrFiles()}
							{this.state.selectedActivityOrFiles === "files" && availableModes.includes("files") &&
								<div style={{ flex: 0, marginLeft: "32px", marginTop: "10px", color: "#a8b8cd" }}>
									<label>Upload File</label>
									<div>
									<input type="file" /*accept=".*" */ onChange={async (event) => {
										console.log("Selected file:", event.target.files[0]);
										try {
											await uploadAiFile(env.s3.aiBucket, "openai/files/" + this.props.user.identityId, event.target.files[0]);
										} catch (e) {
											console.log(e);
										}
									}} />
									</div>
								</div>
							}
						</div>
					}
				</div>

				<div style={{ flex: 1, minHeight: 0, border: "1px solid #2d3f57", borderRadius: "10px", backgroundColor: "#0b121e", display: "flex", flexDirection: "column", overflow: "hidden" }}>
					<div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderBottom: "1px solid #2d3f57", backgroundColor: "#101a2b" }}>
						<Button
							bsStyle="primary"
							style={getTabButtonStyle(responseViewTab === "response")}
							onClick={() => this.setState({ responseViewTab: "response" })}
						>
							Response
						</Button>
						{hasSnippetTab &&
							<Button
								bsStyle="primary"
								style={getTabButtonStyle(responseViewTab === "snippets")}
								onClick={() => this.setState({ responseViewTab: "snippets" })}
							>
								Snippets
							</Button>
						}
					</div>

					<div id="openai-markdown2" style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "12px", color: "#dce8f8" }}>
						{responseViewTab === "snippets"
							? this.renderResponseSnippets(snippetBlocks)
							: <>
								<div dangerouslySetInnerHTML={createMarkupForLabel(this.props.aiAssistantResponse)} />
								{this.state.isWaitingForResponse &&
									<div style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										marginTop: "10px",
										paddingTop: "10px",
										borderTop: "1px dashed #2d3f57",
										color: "#9ec0e8",
										fontStyle: "italic"
									}}>
										<FontAwesomeIcon icon="spinner" spin />
										<span>{this.state.hasReceivedResponseChunk ? "Assistant is responding..." : "Assistant is thinking..."}</span>
									</div>
								}
							</>
						}
					</div>
					<div style={{
						flex: "0 0 auto",
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: "6px",
						padding: "8px 10px",
						borderTop: "1px solid #2d3f57",
						backgroundColor: "#0f1827"
					}}>
						{this.isScriptMode(this.state.selectedActivityOrFiles) &&
							<>
								<Button
									style={{ ...responseActionButtonStyle, backgroundColor: "#255ca1", borderColor: "#3069af" }}
									bsStyle="primary"
									disabled={!canInsertScript}
									onClick={() => this.insertScriptFromAssistant({ replace: false })}
								>
									Insert
								</Button>
								<Button
									style={{ ...responseActionButtonStyle, backgroundColor: "#1d4f8a", borderColor: "#2a629e" }}
									bsStyle="primary"
									disabled={!canInsertScript}
									onClick={() => this.insertScriptFromAssistant({ replace: true })}
								>
									Replace
								</Button>
							</>
						}
						{this.isDraftMode(this.state.selectedActivityOrFiles) &&
							<>
								<Button
									style={{ ...responseActionButtonStyle, backgroundColor: "#255ca1", borderColor: "#3069af" }}
									bsStyle="primary"
									disabled={!canInsertDraft}
									onClick={() => this.insertDraftFromAssistant({ replace: false })}
								>
									Insert
								</Button>
								<Button
									style={{ ...responseActionButtonStyle, backgroundColor: "#1d4f8a", borderColor: "#2a629e" }}
									bsStyle="primary"
									disabled={!canInsertDraft}
									onClick={() => this.insertDraftFromAssistant({ replace: true })}
								>
									Replace
								</Button>
							</>
						}
						<Button
							style={{ ...responseActionButtonStyle, backgroundColor: "#1f3048", borderColor: "#304866" }}
							bsStyle="primary"
							onClick={() => copyToClipboard(this.props.aiAssistantResponseLast)}
						>
							<FontAwesomeIcon icon="copy" /> Copy Full Response
						</Button>
					</div>
				</div>

				<div style={{ flex: "0 0 auto", flexShrink: 0, display: "flex", flexDirection: "column", border: "1px solid #2d3f57", borderRadius: "10px", backgroundColor: "#0f1827", padding: "12px" }}>
					<textarea ref="inputAiMessage" style={{ height: "110px", marginTop: "0px", marginBottom: "10px", borderRadius: "8px", border: "1px solid #58739a", backgroundColor: "#f5f9ff", color: "#1a2535" }} className="form-control" placeholder="Enter your message..." value={this.state.inputAiMessage} onChange={(e) => {
						this.setState({
							inputAiMessage: e.target.value
						});
					}} />
					<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
						<Button disabled={this.state.inputAiMessage === "" || this.state.isWaitingForResponse} style={{ color: "white", backgroundColor: "#2b7de9", borderColor: "#2b7de9", minWidth: "84px", borderRadius: "8px", fontWeight: 600 }} onClick={async () => {
							// getContent(this.props.aiAssistantId, item.id);

							if (this.isScriptMode(this.state.selectedActivityOrFiles)) {
								await this.runScriptModeRequest(this.state.selectedActivityOrFiles);
							} else if (this.isDraftMode(this.state.selectedActivityOrFiles)) {
								await this.runDraftModeRequest(this.state.selectedActivityOrFiles);
							} else if (this.state.selectedActivityOrFiles === "authoring") {
								if (!availableModes.includes("authoring")) {
									this.appendAssistantErrorMessage("Authoring help mode is not available on this screen.");
									return;
								}

								await this.ensureAuthoringContextLoaded();
								const authoringMessage = this.buildAuthoringContextMessage(this.state.inputAiMessage);

								this.appendConversationHeader(this.state.inputAiMessage, "authoring");
								this.startWaitingForResponse();
								try {
									const chatCompletionsTemplate = {
										messages: [
											{
												role: "system",
												content: "You are a helpful assistant."
											},
											{
												role: "user",
												content: authoringMessage
											}
										],
										model: "gpt-4-turbo"
									};

									const authoringResponse = await getChatCompletions(JSON.stringify(chatCompletionsTemplate));
									const responseText = authoringResponse && typeof authoringResponse === "string"
										? authoringResponse
										: "I was unable to generate a response.";

									let newData = this.context.tree.get(["appState", "aiAssistantResponse"]);
									newData = newData + responseText;
										this.context.tree.set(["appState", "aiAssistantResponse"], newData);
										this.context.tree.set(["appState", "aiAssistantResponseLast"], responseText);
									} catch (_err) {
										this.appendAssistantErrorMessage("Failed to process authoring help response.");
									} finally {
										this.stopWaitingForResponse();
									}
								} else if (this.state.selectedActivityOrFiles === "activity") {
								if (!availableModes.includes("activity") || currentActivityId === "") {
									this.appendAssistantErrorMessage("Activity context is not available on this screen.");
									return;
								}

									this.appendConversationHeader(this.state.inputAiMessage, "activity");
									this.startWaitingForResponse();
									getContentStreamed(smd, parser, null, this.props.aiAssistantId, this.state.threadId, this.state.inputAiMessage, state.get(["user", "identityId"]), currentActivityId);
								} else if (this.state.selectedActivityOrFiles === "files") {
								if (!availableModes.includes("files")) {
									this.appendAssistantErrorMessage("File-based assistant mode is only available while authoring an activity with AI enabled.");
									return;
								}

								if (!this.props.aiAssistantId || this.props.aiAssistantId === "") {
									this.appendAssistantErrorMessage("AI Assistant is not initialized for this activity. Toggle AI Assistant off/on in category properties and try again.");
									return;
								}

								let threadId = this.state.threadId;
								if (!threadId) {
									threadId = await this.createThreadIfNeeded();
								}
								if (!threadId) {
									this.appendAssistantErrorMessage("Unable to initialize assistant thread. Please try again.");
									return;
								}

									this.appendConversationHeader(this.state.inputAiMessage, "files");
									this.startWaitingForResponse();
									getContentStreamed(smd, parser, null, this.props.aiAssistantId, threadId, this.state.inputAiMessage);
								} else if (this.state.selectedActivityOrFiles === "org") {
									this.appendConversationHeader(this.state.inputAiMessage, "org");
									this.startWaitingForResponse();
									getContentStreamed(smd, parser, null, this.props.aiAssistantId, this.state.threadId, this.state.inputAiMessage, state.get(["user", "identityId"]), currentActivityId, getOrgId());
							}
						}}>
							{this.state.isWaitingForResponse ?
								<span><FontAwesomeIcon icon="spinner" spin />&nbsp;Working</span>
								: "Run"
							}
						</Button>
						{this.isDraftMode(this.state.selectedActivityOrFiles) &&
							<div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
								<Button
									style={{ ...responseActionButtonStyle, backgroundColor: "#274567", borderColor: "#365b87", whiteSpace: "nowrap" }}
									bsStyle="primary"
									bsSize="small"
									disabled={!canInsertPromptFromField}
									title="Insert focused field into prompt"
									onClick={() => this.insertPromptFromFocusedInput({ replace: false })}
								>
									Insert Field
								</Button>
								<Button
									style={{ ...responseActionButtonStyle, backgroundColor: "#1f3a58", borderColor: "#2f5178", whiteSpace: "nowrap" }}
									bsStyle="primary"
									bsSize="small"
									disabled={!canInsertPromptFromField}
									title="Replace prompt with focused field"
									onClick={() => this.insertPromptFromFocusedInput({ replace: true })}
								>
									Replace Field
								</Button>
							</div>
						}
					</div>

				</div>

			</div>
			// </div>
		);
	}
}

AiAssistant.contextTypes = {
	tree: PropTypes.baobab
};

export default branch({
	user: ["user"],
	activityId: ["tree", "root", "id"],
	aiAssistantId: ["tree", "root", "entity", "aiAssistantId"],
	rootAssistantFiles: ["tree", "root", "entity", "aiAssistantFiles"],
	rootAssistantChecklistFiles: ["tree", "root", "entity", "aiAssistantChecklistFiles"],
	aiAssistantResponse: ["appState", "aiAssistantResponse"],
	aiAssistantResponseLast: ["appState", "aiAssistantResponseLast"],
	aiAssistantLaunchRequest: ["appState", "aiAssistantLaunchRequest"],
	aiAssistantFocusedTarget: ["appState", "aiAssistantFocusedTarget"]
	// newFiles: ['tree', 'root', 'entity', 'aiAssistantFiles']
}, AiAssistant);
